/**
 * Life4Earth V2 — Reviewer-skill ↔ content-needing-review match scoring
 * (DEV-1379, Content Verification Stage 3).
 *
 * A CLONE of src/Data/UserResources/resourceMatch.ts's banded/explainable
 * shape — NOT a generalization of it (SPEC §9 decision 7: clone for v1, file a
 * follow-on to generalize into the shared matcher later). One transparent
 * engine, reasoning exposed ("algorithms are open"): a reviewer's registered
 * skill LABELS are matched against each pool item's matchText by shared keywords.
 *
 * Match-text source (DEV-1396): when the author declared SUBJECT tags, the server
 * builds matchText from those subjects' platform-skill LABELS — the (stronger)
 * required-skill set, a DIRECT match to reviewer endorsements (subjects ARE
 * platform_skills slugs; ONE vocabulary, NO mapping layer). Items with no subjects
 * fall back to the derived title + category + tags. The matcher is source-agnostic:
 * it tokenizes whatever matchText the server supplies, so the subject signal flows
 * through this same transparent engine unchanged. Original signal: shared keywords → { strong | partial | low } + sharedKeywords.
 *
 * Design notes:
 *   - No category/proximity/priority axes (those are resource-domain concepts).
 *     The only signal here is keyword overlap between the reviewer's skills and
 *     the item's match text, so the weights collapse to a keyword-count band.
 *   - The weights encode a product value judgment and are centralized +
 *     overridable, mirroring resourceMatch's DEFAULT_WEIGHTS pattern.
 *   - Pure and side-effect-free: same inputs always produce the same output.
 */

export type ReviewMatchBucket = 'strong' | 'partial' | 'low';

/* ------------------------------------------------------------------ */
/*  Tokenization (cloned from resourceMatch — light plural stem)       */
/* ------------------------------------------------------------------ */

const STOPWORDS = new Set([
  'the', 'and', 'for', 'our', 'your', 'any', 'some', 'with', 'from', 'this',
  'that', 'are', 'was', 'will', 'can', 'need', 'needs', 'needed', 'help',
  'looking', 'seeking', 'want', 'wanted', 'please', 'available', 'offer',
  'one', 'two', 'few', 'per', 'use', 'used', 'using', 'into', 'out', 'about',
]);

/**
 * Lowercase, strip punctuation, drop short/stopword tokens, apply a light
 * plural stem. Returns DISTINCT tokens.
 */
export function tokenize(text: string | null | undefined): string[] {
  if (!text) return [];
  const raw = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const out = new Set<string>();
  for (const t of raw) {
    if (t.length < 3 || STOPWORDS.has(t)) continue;
    const stem = t.length > 3 && t.endsWith('s') ? t.slice(0, -1) : t;
    out.add(stem);
  }
  return [...out];
}

/* ------------------------------------------------------------------ */
/*  Weights (the tunable product value judgment)                       */
/* ------------------------------------------------------------------ */

export interface ReviewMatchWeights {
  /** Points per distinct shared keyword. */
  keywordEach: number;
  /** Max points contributable by keyword overlap. */
  keywordCap: number;
  /** total >= this → 'strong'. */
  strongThreshold: number;
  /** total >= this (and < strong) → 'partial'; else 'low'. */
  partialThreshold: number;
}

export const DEFAULT_REVIEW_WEIGHTS: ReviewMatchWeights = Object.freeze({
  keywordEach: 34,
  keywordCap: 100,
  // 2+ shared keywords → strong; exactly 1 → partial; 0 → low.
  strongThreshold: 68,
  partialThreshold: 34,
});

/* ------------------------------------------------------------------ */
/*  Result                                                             */
/* ------------------------------------------------------------------ */

export interface ReviewMatchScore {
  total: number;
  bucket: ReviewMatchBucket;
  /** Per-factor contributions, for tooltips / "why this match". */
  parts: { keyword: number };
  /** Distinct keywords that overlapped (for explainability). */
  sharedKeywords: string[];
}

/* ------------------------------------------------------------------ */
/*  Scoring                                                            */
/* ------------------------------------------------------------------ */

export interface ReviewScoreOptions {
  weights?: Partial<ReviewMatchWeights>;
}

function resolveWeights(override?: Partial<ReviewMatchWeights>): ReviewMatchWeights {
  if (!override) return DEFAULT_REVIEW_WEIGHTS;
  return { ...DEFAULT_REVIEW_WEIGHTS, ...override };
}

/** Bucket a raw total. */
export function bucketFor(
  total: number,
  weights: ReviewMatchWeights = DEFAULT_REVIEW_WEIGHTS,
): ReviewMatchBucket {
  if (total >= weights.strongThreshold) return 'strong';
  if (total >= weights.partialThreshold) return 'partial';
  return 'low';
}

/**
 * Score a set of reviewer skill labels against one item's match text. Pure.
 * The reviewer's skills are joined into one token bag; the shared tokens with
 * the item's match text drive the band.
 */
export function scoreSkillsAgainstItem(
  skillLabels: readonly string[],
  itemMatchText: string,
  opts: ReviewScoreOptions = {},
): ReviewMatchScore {
  const w = resolveWeights(opts.weights);

  const skillTokens = new Set(tokenize(skillLabels.join(' ')));
  const itemTokens = new Set(tokenize(itemMatchText));
  const sharedKeywords = [...skillTokens].filter((t) => itemTokens.has(t));

  const keywordPts = Math.min(sharedKeywords.length * w.keywordEach, w.keywordCap);
  const total = keywordPts;

  return {
    total,
    bucket: bucketFor(total, w),
    parts: { keyword: keywordPts },
    sharedKeywords,
  };
}
